import React, { useLayoutEffect, useState, useContext } from 'react';
import { rawTimeZones } from '@vvo/tzdb';

import * as S from './styles';
import Modal from 'components/Modal';
import { BannerFromCarrosselBannersProps } from 'types/BannerFromCarrosselBannersProps';
import InputImgBigger from 'components/InputImgBigger';
import SimpleInput from 'components/SimpleInput';
import { SelectInputItemProps } from 'types/SelectInputItemProps';
import SelectInput from 'components/SelectInput';
import { optionsRedirectType } from 'utils/optionsInputs';
import {
	generateUid,
	formatDateToISO,
	formatToDateCustom
} from 'utils/functions';
import { verifyIfFormCanSave } from './functions';
import SystemNotifyContext from 'context/SystemNotifyContext';
import Check from 'components/Buttons/Check';
import SimpleTextArea from 'components/SimpleTextArea';
import { color } from 'styles/pallete';

interface FormAddBannerProps {
	optionsDynamicScreen: SelectInputItemProps[];
	isOpen: boolean;
	setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
	banner: BannerFromCarrosselBannersProps | null;
	functionToGetDataAboutBanner: any;
	sizeBanner: { width: number; height: number };
	withRne?: boolean;
	withTitle?: boolean;
	useDescription?: boolean;
}

export interface DataFormAddBannerProps {
	idFront: string | number;
	banner: { id: string; relativePath: string } | null;
	redirectType: string;
	redirectValue: string;
	rne?: string;
	title?: string;
	schedule?: {
		countdown: boolean;
		start: string;
		end: string;
		timezone: string;
	};
	description?: {
		title: string;
		subTitle: string;
		descriptionText: string;
	};
	query: string;
}

const initialState: DataFormAddBannerProps = {
	idFront: '',
	banner: null,
	redirectType: '',
	redirectValue: '',
	schedule: {
		countdown: false,
		start: '',
		end: '',
		timezone: ''
	},
	query: ''
};

export default function FormAddBanner({
	isOpen,
	optionsDynamicScreen,
	setIsOpen,
	banner,
	functionToGetDataAboutBanner = () => {},
	withTitle = true,
	withRne = false,
	sizeBanner,
	useDescription = false
}: FormAddBannerProps) {
	initialState.idFront = generateUid();

	const { handleNotify } = useContext(SystemNotifyContext);
	const formatGMT = (offsetMinutes: number): string => {
		const hours = Math.floor(offsetMinutes / 60);
		const minutes = Math.abs(offsetMinutes % 60);
		const sign = offsetMinutes >= 0 ? '+' : '-';
		return `GMT${sign}${Math.abs(hours)}${minutes ? `:${minutes}` : ''}`;
	};

	const [dataForm, setDataForm] = useState<DataFormAddBannerProps>({
		...initialState
	});
	const [isShowDateInputs, setIsShowDateInputs] = useState(false);
	const [timeZoneOptions, setTimeZoneOptions] = useState<
		SelectInputItemProps[]
	>([]);

	function findeOptionOnListByNumber(
		value: string,
		list: SelectInputItemProps[]
	) {
		return list.find((item) => item.id === value) || '';
	}

	function findOptionOnListById(value: string, list: SelectInputItemProps[]) {
		return list.find((item) => item.id === value) || '';
	}

	function saveBanner() {
		const newObj = dataForm;

		const canSave = verifyIfFormCanSave(
			dataForm,
			withTitle,
			handleNotify,
			useDescription
		);

		if (
			dataForm.schedule &&
			dataForm.schedule.start === '' &&
			dataForm.schedule.end === ''
		) {
			delete newObj.schedule;
		}

		if (canSave) {
			functionToGetDataAboutBanner(newObj);
			setIsOpen(false);
			setDataForm({ ...initialState, idFront: generateUid() });
			setIsShowDateInputs(false);
		}
	}

	function cancel() {
		setIsOpen(false);
		setDataForm({ ...initialState, idFront: generateUid() });
		setIsShowDateInputs(false);
	}

	//tira o agendamento quando check de schedule é colocado como false
	useLayoutEffect(() => {
		if (!isShowDateInputs) {
			setDataForm((obj) => ({
				...obj,
				schedule: {
					start: '',
					end: '',
					countdown: false,
					timezone: ''
				}
			}));
		}
	}, [isShowDateInputs]);

	useLayoutEffect(() => {
		const newListTimeZoneOptions: SelectInputItemProps[] = rawTimeZones.map(
			(tz) => ({
				id: tz.name,
				option: `${tz.name} (${formatGMT(tz.rawOffsetInMinutes)})`
			})
		);
		setTimeZoneOptions(newListTimeZoneOptions);
	}, []);

	//caso seja em um banner com descrição, ja adiciona o estado inicial
	useLayoutEffect(() => {
		if (useDescription) {
			setDataForm((obj) => ({
				...obj,
				description: { title: '', descriptionText: '', subTitle: '' }
			}));
		}
	}, []);

	//na edição carrega os dados do banner que será editado
	useLayoutEffect(() => {
		if (banner !== null) {
			setTimeout(() => {
				const bannerToEdit: DataFormAddBannerProps = {
					idFront: banner?.idFront || '',
					query: banner.query,
					banner: {
						id: banner.image.id,
						relativePath: banner.image.relativePath
					},
					redirectType: banner.redirect.type,
					redirectValue: banner.redirect.value,
					title: banner.bannerTitle || '',
					schedule: banner?.schedule
						? banner?.schedule
						: {
								countdown: false,
								start: '',
								end: '',
								timezone: ''
							}
				};

				if (useDescription && banner?.description) {
					bannerToEdit.description = banner?.description;
				}

				if (banner?.rne) {
					bannerToEdit.rne = banner?.rne;
				}

				setDataForm(bannerToEdit);

				if (
					bannerToEdit.schedule &&
					(bannerToEdit.schedule.start !== '' ||
						bannerToEdit.schedule.end !== '')
				) {
					setIsShowDateInputs(true);
				}
			}, 100);
		}
	}, [banner]);

	return (
		<Modal
			isOpenProps={isOpen}
			setIsOpenModal={() => {
				setIsOpen((value) => !value);
				setDataForm({ ...initialState, idFront: generateUid() });
				setIsShowDateInputs(false);
			}}
			title={banner === null ? 'Adicionar Banner' : 'Editar Banner'}
			buttonClose={false}
		>
			<S.container>
				<S.form>
					{withTitle && (
						<SimpleInput
							label="Título"
							initialValue={dataForm.title}
							getValue={(value: string) =>
								setDataForm((obj) => ({ ...obj, title: value }))
							}
						/>
					)}

					{useDescription && (
						<>
							<S.twoInputs>
								<SimpleInput
									label="Título do banner"
									initialValue={dataForm?.description?.title || ''}
									getValue={(value: string) =>
										setDataForm((obj) => ({
											...obj,
											description: { ...obj.description!, title: value }
										}))
									}
								/>
								<SimpleInput
									label="Subtítulo do banner"
									initialValue={dataForm?.description?.subTitle}
									getValue={(value: string) =>
										setDataForm((obj) => ({
											...obj,
											description: { ...obj.description!, subTitle: value }
										}))
									}
								/>
							</S.twoInputs>

							<SimpleTextArea
								colorFont={color.interface.lightGray}
								labelColor={color.interface.lightGray}
								borderColor={color.interface.lightGray2}
								textArea={dataForm.description?.descriptionText || ''}
								getValue={(value: string) => {
									setDataForm((obj) => ({
										...obj,
										description: {
											...obj.description!,
											descriptionText: value
										}
									}));
								}}
								title={'Descrição do banner'}
								rows={6}
							/>
						</>
					)}

					<S.twoInputs>
						<SelectInput
							list={optionsRedirectType}
							getValue={(value: SelectInputItemProps | '') =>
								setDataForm((obj) => ({
									...obj,
									redirectType: value === '' ? value : (value?.id as string)
								}))
							}
							initialValue={
								dataForm.redirectValue === ''
									? ''
									: findeOptionOnListByNumber(
											dataForm.redirectType,
											optionsRedirectType
										)
							}
							label="Tipo de redirecionamento"
						/>

						{['', 'product', 'url', 'webview', 'query', 'screen_app'].includes(
							dataForm.redirectType
						) && (
							<SimpleInput
								label="Valor do redirecionamento"
								initialValue={dataForm.redirectValue}
								getValue={(value: string) =>
									setDataForm((obj) => ({ ...obj, redirectValue: value }))
								}
							/>
						)}

						{dataForm.redirectType === 'app_screen' && (
							<SelectInput
								list={optionsDynamicScreen}
								getValue={(value: SelectInputItemProps | '') =>
									setDataForm((obj) => ({
										...obj,
										redirectValue: value === '' ? value : (value?.id as string)
									}))
								}
								initialValue={findOptionOnListById(
									dataForm.redirectValue,
									optionsDynamicScreen
								)}
								label="Valor redirecionamento *"
							/>
						)}
					</S.twoInputs>

					{withRne && (
						<SimpleInput
							label="RNE"
							initialValue={dataForm.rne}
							getValue={(value: string) =>
								setDataForm((obj) => ({ ...obj, rne: value }))
							}
						/>
					)}

					<SimpleInput
						label="Query"
						initialValue={dataForm.query}
						getValue={(value: string) =>
							setDataForm((obj) => ({ ...obj, query: value }))
						}
					/>

					<InputImgBigger
						maxHeight={sizeBanner.height}
						minHeight={sizeBanner.height === 9999 ? 1 : sizeBanner.height}
						maxWidth={sizeBanner.width}
						minWidth={sizeBanner.width === 9999 ? 1 : sizeBanner.width}
						legendDimensionsImage={
							sizeBanner.width === 9999
								? null
								: {
										width: `${sizeBanner.width}px`,
										height: `${sizeBanner.height}px`
									}
						}
						label="Banner"
						idImagem="FormAddBanner"
						initialImage={dataForm.banner?.relativePath || ''}
						getImage={(value: any) => {
							setDataForm((obj) => ({
								...obj,
								banner: {
									id: value?.id || generateUid(),
									relativePath: value?.url || value.relativePath
								}
							}));
						}}
					/>

					<Check
						text="Programação"
						isChecked={isShowDateInputs}
						setIsChecked={() => {
							setIsShowDateInputs((obj) => !obj);
						}}
					/>

					{isShowDateInputs && dataForm.schedule && (
						<>
							<SelectInput
								isHide
								list={timeZoneOptions}
								getValue={(value: SelectInputItemProps | '') =>
									setDataForm((obj) => ({
										...obj,
										schedule: {
											...obj.schedule!,
											timezone: value === '' ? '' : (value?.id as string)
										}
									}))
								}
								initialValue={findOptionOnListById(
									dataForm.schedule.timezone,
									timeZoneOptions
								)}
								label="Selecione o timezone"
							/>

							<S.twoInputs>
								<SimpleInput
									typeInput="datetime-local"
									label="Início"
									initialValue={formatToDateCustom(
										dataForm.schedule.start || ''
									)}
									getValue={(value: string) =>
										setDataForm((obj) => ({
											...obj,
											schedule: {
												...obj.schedule!,
												start: formatDateToISO(value)
											}
										}))
									}
									paddingInLineEnd
								/>

								<SimpleInput
									typeInput="datetime-local"
									label="Fim"
									initialValue={formatToDateCustom(dataForm.schedule.end || '')}
									getValue={(value: string) =>
										setDataForm((obj) => ({
											...obj,
											schedule: {
												...obj.schedule!,
												end: formatDateToISO(value)
											}
										}))
									}
									paddingInLineEnd
								/>
							</S.twoInputs>
						</>
					)}
				</S.form>

				<S.containerBtns>
					<S.cancel onClick={cancel}>Cancelar</S.cancel>
					<S.save onClick={saveBanner}>Salvar</S.save>
				</S.containerBtns>
			</S.container>
		</Modal>
	);
}
